import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faTrashCan,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { Dialog, DialogContent, DialogTitle } from "frontend-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TaskImage, TaskWithOccurrences } from "@dashboard/api/task";
import { useDeleteTaskImage } from "@dashboard/hooks/useTaskImageQuery";
import { Can } from "@dashboard/casl/taskAbility";
import { subject } from "@casl/ability";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";

interface TaskImagePreviewProps {
    images: TaskImage[];
    imageUrls: Map<number, string>;
    initialIndex?: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    task: TaskWithOccurrences;
}

export function TaskImagePreview({
    images,
    imageUrls,
    initialIndex = 0,
    open,
    onOpenChange,
    task,
}: TaskImagePreviewProps) {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [prevOpen, setPrevOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const deleteMutation = useDeleteTaskImage();

    if (open !== prevOpen) {
        setPrevOpen(open);
        if (open) {
            setCurrentIndex(initialIndex);
        }
    }

    useEffect(() => {
        if (!open) return;

        const adjacentIndices = [currentIndex - 1, currentIndex + 1].filter(
            (i) => i >= 0 && i < images.length,
        );

        adjacentIndices.forEach((idx) => {
            const image = images[idx];
            const url = imageUrls.get(image.id);
            if (url) {
                const img = new Image();
                img.src = url;
            }
        });
    }, [currentIndex, images, imageUrls, open]);

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;

    const currentImage = images[currentIndex];
    const currentUrl = currentImage ? imageUrls.get(currentImage.id) : null;

    const handleDelete = async () => {
        if (!currentImage) return;
        await deleteMutation.mutateAsync({ imageId: currentImage.id, taskId: currentImage.taskId });
        if (images.length === 1) {
            onOpenChange(false);
        } else {
            setCurrentIndex((i) => Math.max(0, i - 1));
        }
    };

    const overlayButtonClass =
        "z-10 flex items-center justify-center rounded-full bg-compendia-card/80 hover:bg-compendia-card border border-compendia-border text-compendia-foreground cursor-pointer transition-colors";

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    aria-describedby={undefined}
                    showCloseButton={false}
                    className="max-w-3xl sm:max-w-3xl w-full p-0 gap-0 overflow-hidden"
                >
                    <DialogTitle className="sr-only">{t("list.imagePreview")}</DialogTitle>
                    <div className="relative flex items-center justify-center min-h-64 bg-compendia-foreground/5">
                        <button
                            type="button"
                            className={`${overlayButtonClass} absolute top-2 right-2 w-8 h-8`}
                            onClick={() => onOpenChange(false)}
                            aria-label={t("common.close")}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>

                        {currentImage && (
                            <Can
                                I="delete"
                                this={subject("TaskImage", {
                                    createdById: currentImage.createdById,
                                    task,
                                })}
                            >
                                <button
                                    type="button"
                                    className={`${overlayButtonClass} absolute top-2 left-2 w-8 h-8 disabled:opacity-60 disabled:cursor-not-allowed`}
                                    onClick={() => setConfirmDeleteOpen(true)}
                                    disabled={deleteMutation.isPending}
                                    aria-label={t("list.deleteImage")}
                                >
                                    <FontAwesomeIcon icon={faTrashCan} size="sm" />
                                </button>
                            </Can>
                        )}

                        {hasPrev && (
                            <button
                                type="button"
                                className={`${overlayButtonClass} absolute left-2 w-9 h-9`}
                                onClick={() => setCurrentIndex((i) => i - 1)}
                                aria-label={t("common.previous")}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                        )}

                        <div className="flex items-center justify-center w-full h-full p-8 min-h-64">
                            {!currentUrl && (
                                <span className="text-compendia-muted text-sm">
                                    {t("common.loading")}
                                </span>
                            )}
                            {currentUrl && (
                                <img
                                    src={currentUrl}
                                    alt={currentImage?.fileName ?? ""}
                                    className="max-h-[70vh] max-w-full object-contain rounded-[3px]"
                                />
                            )}
                        </div>

                        {hasNext && (
                            <button
                                type="button"
                                className={`${overlayButtonClass} absolute right-2 w-9 h-9`}
                                onClick={() => setCurrentIndex((i) => i + 1)}
                                aria-label={t("common.next")}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        )}
                    </div>

                    {images.length > 1 && (
                        <div className="flex justify-center gap-1 py-2 bg-compendia-card border-t border-compendia-border">
                            {images.map((_, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                                        idx === currentIndex
                                            ? "bg-compendia-foreground"
                                            : "bg-compendia-border hover:bg-compendia-muted"
                                    }`}
                                    onClick={() => setCurrentIndex(idx)}
                                    aria-label={`Go to image ${idx + 1}`}
                                />
                            ))}
                        </div>
                    )}

                    {currentImage && (
                        <div className="px-4 py-2 text-xs text-compendia-muted border-t border-compendia-border text-center">
                            {currentImage.fileName}
                            {images.length > 1 && ` (${currentIndex + 1} / ${images.length})`}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={handleDelete}
                title={t("list.deleteImage")}
                description={t("list.confirmDeleteImage")}
            />
        </>
    );
}
